import { useQuery } from '@tanstack/react-query';
import { Building2, Users, FileText, AlertCircle, RefreshCw } from 'lucide-react';
import { sitesApi } from '../api/sites.api';
import { useSyncStatus } from '../hooks/useSyncStatus';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function SitesPage() {
  const { isSyncing } = useSyncStatus();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list(),
    refetchInterval: isSyncing ? 5000 : false,
  });

  const sites = data?.sites ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
          <p className="text-gray-600">
            CRM sites synced in step 1 of the background pipeline (sites → subscribers → invoices)
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total sites</p>
          <p className="text-2xl font-bold text-gray-900">{sites.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total subscribers (local)</p>
          <p className="text-2xl font-bold text-gray-900">
            {sites.reduce((s, x) => s + x.subscriberCount, 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Unpaid invoices (local)</p>
          <p className="text-2xl font-bold text-red-600">
            {sites.reduce((s, x) => s + x.unpaidInvoiceCount, 0)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading sites...</div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-red-500" />
            <p className="mt-2 text-red-600">Failed to load sites</p>
          </div>
        ) : sites.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No sites yet. Wait for the next scheduled CRM sync or queue one from Settings.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    CRM site ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Subscribers
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Invoices
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Unpaid
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last invoice sync
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sites.map((site) => (
                  <tr key={site.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm text-gray-900">{site.crmSiteId}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{site.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                        <Users className="w-3.5 h-3.5" />
                        {site.subscriberCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                        <FileText className="w-3.5 h-3.5" />
                        {site.invoiceCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-red-600">
                      {site.unpaidInvoiceCount}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(site.lastInvoiceSyncAt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          site.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {site.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default SitesPage;
